"use client";

import { Check, Copy, Share2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";

type Channel =
	| "whatsapp"
	| "linkedin"
	| "twitter"
	| "facebook"
	| "telegram"
	| "email"
	| "copy"
	| "native";

type Props = {
	jobId: string;
	jobTitle: string;
	company: string | null;
	shareUrl: string;
	labels: {
		heading: string;
		subheading: string;
		copy: string;
		copied: string;
		share: string;
		whatsapp: string;
		linkedin: string;
		twitter: string;
		facebook: string;
		telegram: string;
		email: string;
		emailSubject: string;
	};
};

function buildShareText(jobTitle: string, company: string | null): string {
	return company
		? `${jobTitle} — ${company} (remote, paid in USD via Loker Dollar)`
		: `${jobTitle} (remote, paid in USD via Loker Dollar)`;
}

export function JobShareLinks({
	jobId,
	jobTitle,
	company,
	shareUrl,
	labels,
}: Props) {
	const posthog = usePostHog();
	const [copied, setCopied] = useState(false);
	const [canNativeShare, setCanNativeShare] = useState(false);

	useEffect(() => {
		setCanNativeShare(
			typeof navigator !== "undefined" && typeof navigator.share === "function",
		);
	}, []);

	const text = buildShareText(jobTitle, company);
	const encodedUrl = encodeURIComponent(shareUrl);
	const encodedText = encodeURIComponent(text);
	const encodedSubject = encodeURIComponent(
		labels.emailSubject.replace("{title}", jobTitle),
	);

	const track = useCallback(
		(channel: Channel) => {
			posthog?.capture("job_share_clicked", {
				job_id: jobId,
				channel,
			});
		},
		[posthog, jobId],
	);

	const handleCopy = useCallback(async () => {
		track("copy");
		try {
			await navigator.clipboard.writeText(shareUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			setCopied(false);
		}
	}, [shareUrl, track]);

	const handleNativeShare = useCallback(async () => {
		track("native");
		try {
			await navigator.share({ title: jobTitle, text, url: shareUrl });
		} catch {
			// User dismissed or share unsupported — silent fail.
		}
	}, [jobTitle, text, shareUrl, track]);

	const linkClass =
		"flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-card text-muted-foreground transition-all hover:scale-105 hover:border-border hover:text-foreground hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

	return (
		<div className="space-y-2">
			<div>
				<h3 className="text-sm font-semibold text-foreground">
					{labels.heading}
				</h3>
				<p className="text-xs text-muted-foreground">{labels.subheading}</p>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				<a
					href={`https://wa.me/?text=${encodedText}%20${encodedUrl}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => track("whatsapp")}
					className={linkClass}
					aria-label={labels.whatsapp}
					title={labels.whatsapp}
				>
					<WhatsAppIcon />
				</a>
				<a
					href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => track("linkedin")}
					className={linkClass}
					aria-label={labels.linkedin}
					title={labels.linkedin}
				>
					<LinkedInIcon />
				</a>
				<a
					href={`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => track("twitter")}
					className={linkClass}
					aria-label={labels.twitter}
					title={labels.twitter}
				>
					<XIcon />
				</a>
				<a
					href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => track("telegram")}
					className={linkClass}
					aria-label={labels.telegram}
					title={labels.telegram}
				>
					<TelegramIcon />
				</a>
				<a
					href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => track("facebook")}
					className={linkClass}
					aria-label={labels.facebook}
					title={labels.facebook}
				>
					<FacebookIcon />
				</a>
				<a
					href={`mailto:?subject=${encodedSubject}&body=${encodedText}%0A%0A${encodedUrl}`}
					onClick={() => track("email")}
					className={linkClass}
					aria-label={labels.email}
					title={labels.email}
				>
					<EmailIcon />
				</a>
				<button
					type="button"
					onClick={handleCopy}
					aria-label={copied ? labels.copied : labels.copy}
					title={copied ? labels.copied : labels.copy}
					className={linkClass}
				>
					{copied ? (
						<Check className="h-4 w-4 text-success" />
					) : (
						<Copy className="h-4 w-4" />
					)}
				</button>
				{canNativeShare && (
					<button
						type="button"
						onClick={handleNativeShare}
						aria-label={labels.share}
						title={labels.share}
						className={linkClass}
					>
						<Share2 className="h-4 w-4" />
					</button>
				)}
			</div>
		</div>
	);
}

function WhatsAppIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden="true"
			className="h-4 w-4"
			fill="currentColor"
		>
			<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
		</svg>
	);
}

function LinkedInIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden="true"
			className="h-4 w-4"
			fill="currentColor"
		>
			<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.778 13.019H3.555V9h3.56v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.226.792 24 1.771 24h20.451C23.2 24 24 23.226 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
		</svg>
	);
}

function XIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden="true"
			className="h-4 w-4"
			fill="currentColor"
		>
			<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
		</svg>
	);
}

function FacebookIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden="true"
			className="h-4 w-4"
			fill="currentColor"
		>
			<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
		</svg>
	);
}

function TelegramIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden="true"
			className="h-4 w-4"
			fill="currentColor"
		>
			<path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
		</svg>
	);
}

function EmailIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden="true"
			className="h-4 w-4"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<rect width="20" height="16" x="2" y="4" rx="2" />
			<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
		</svg>
	);
}
